import { useEffect, useState } from 'react'
import DonationsChart from '../components/DonationsChart'
import { API_URL } from '../../constants'
import MonthlyDonationsChart from '../components/MonthlyDonationsChart'
import { Button } from '../../shared/components/Button'
import { exportToExcel } from '../../util/exportFunction'
import { useTranslation } from 'react-i18next'

const useFetchData = endpoint => {
    const [data, setData] = useState(null)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const { t } = useTranslation()

    useEffect(() => {
        const token = localStorage.getItem('token')

        const fetchData = async () => {
            try {
                const response = await fetch(`${API_URL}${endpoint}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Accept-Language': 'en',
                    },
                })

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const result = await response.json()
                setData(result)
            } catch (err) {
                console.error('Fetch error:', err)
                setError(err.message)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [endpoint])

    return { data, error, loading }
}

const DonationsSummary = ({ totalIncome, donationsCount, averageDonation }) => {
    const { t } = useTranslation()
    return (
        <div className='p-4 bg-white shadow-md rounded-xl sm:p-6'>
            <div className='flex flex-row items-center justify-between mb-4 space-y-0'>
                <h2 className='text-lg font-semibold text-gray-800 sm:text-xl'>
                    {t('dashboard.summary')}
                </h2>
                <Button
                    size='small'
                    className='w-auto'
                    onClick={() =>
                        exportToExcel(
                            [
                                {
                                    total_income: totalIncome,
                                    donations_count: donationsCount,
                                    average_donation: averageDonation,
                                },
                            ],
                            'Summary'
                        )
                    }
                >
                    {t('status.export')}
                </Button>
            </div>

            <div className='grid grid-cols-1 gap-3 text-center sm:grid-cols-3 sm:gap-6'>
                <div className='p-3 rounded-lg bg-gray-50 sm:p-4'>
                    <p className='text-xs text-gray-500 sm:text-sm'>
                        {t('dashboard.total_income')}
                    </p>
                    <p className='text-base font-semibold text-green-600 sm:text-lg'>
                        ${parseFloat(totalIncome).toFixed(2)}
                    </p>
                </div>
                <div className='p-3 rounded-lg bg-gray-50 sm:p-4'>
                    <p className='text-xs text-gray-500 sm:text-sm'>
                        {t('dashboard.donations_count')}
                    </p>
                    <p className='text-base font-semibold text-yellow-600 sm:text-lg'>
                        {donationsCount}
                    </p>
                </div>
                <div className='p-3 rounded-lg bg-gray-50 sm:p-4'>
                    <p className='text-xs text-gray-500 sm:text-sm'>
                        {t('dashboard.average_donation')}
                    </p>
                    <p className='text-base font-semibold text-purple-600 sm:text-lg'>
                        ${averageDonation.toFixed(2)}
                    </p>
                </div>
            </div>
        </div>
    )
}

const Dashboard = () => {
    const { t } = useTranslation()

    const {
        data: yearlyData,
        error: yearlyError,
        loading: yearlyLoading,
    } = useFetchData('/admin/dashboard/data')

    const {
        data: monthlyData,
        error: monthlyError,
        loading: monthlyLoading,
    } = useFetchData('/admin/dashboard/monthly')

    const {
        data: summaryData,
        error: summaryError,
        loading: summaryLoading,
    } = useFetchData('/admin/dashboard/summary')

    const {
        data: mostNeededData,
        error: mostNeededError,
        loading: mostNeededLoading,
    } = useFetchData('/admin/dashboard/where-most-needed')

    const isLoading = yearlyLoading || monthlyLoading || summaryLoading
    const error = yearlyError || monthlyError || summaryError

    return (
        <div className='w-full px-2 py-4 space-y-4 sm:px-4 sm:py-6 md:px-6 md:space-y-6'>
            {isLoading && <p className='text-center'>{t('loading')}</p>}
            {error && <p className='text-center text-red-500'>Error: {error}</p>}

            {!isLoading && !error && (
                <div className='space-y-4'>
                    <DonationsSummary
                        totalIncome={summaryData.data.total_income}
                        donationsCount={summaryData.data.donations_count}
                        averageDonation={summaryData.data.average_donation}
                    />

                    <div className='w-full p-3 overflow-x-auto bg-white border shadow-md rounded-xl sm:p-4'>
                        <div className='flex justify-end mb-3'>
                            <Button
                                size='small'
                                onClick={() => exportToExcel(yearlyData, 'yearly_data')}
                                variant={isLoading || error ? 'disabled' : 'primary'}
                            >
                                {t('status.export')}
                            </Button>
                        </div>
                        <div className='min-w-[300px]'>
                            <DonationsChart data={yearlyData} />
                        </div>
                    </div>

                    <div className='w-full p-3 overflow-x-auto bg-white border shadow-md rounded-xl sm:p-4'>
                        <div className='flex justify-end mb-3'>
                            <Button
                                size='small'
                                onClick={() =>
                                    exportToExcel(mostNeededData, 'where_most_needed_data')
                                }
                                variant={isLoading || error ? 'disabled' : 'primary'}
                            >
                                {t('status.export')}
                            </Button>
                        </div>
                        <div className='min-w-[300px]'>
                            <DonationsChart data={mostNeededData} isMostNeeded={true} />
                        </div>
                    </div>

                    {/* <div className='w-full p-3 overflow-x-auto bg-white border shadow-md rounded-xl sm:p-4'>
                        <div className='flex justify-end mb-3'>
                            <Button
                                size='small'
                                onClick={() => exportToExcel(summaryData, 'summary_data')}
                                variant={isLoading || error ? 'disabled' : 'primary'}
                            >
                                {t('status.export')}
                            </Button>
                        </div>
                        <div className='min-w-[300px]'>
                            <MonthlyDonationsChart
                                data={monthlyData.data}
                                year={monthlyData.year}
                            />
                        </div>
                    </div> */}
                </div>
            )}
        </div>
    )
}

export default Dashboard
